import type { PageWithContent, DealroomPublished, PageWithoutContent } from '@getaccept/dsr-shared';
import type { EditorBlock, LoadImagePayload } from '@getaccept/editor-lib';
import {
  getLoadImageURLPayloads,
  setImageSrcURL,
  useLoadedImagesStore,
  useLoadedVideosStore,
} from '@getaccept/editor-lib';
import { ImageHeight } from '@getaccept/lib-shared/src/enums/image-height';
import type { Ref } from 'vue';
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRevisionsStore } from '../store/revisions.store';
import { DsrService } from '../common/dealroom/api/dsr.service';
import { useSessionStore } from '../session/store/session.store';
import { VideoService } from '../feed/api/video.service';

export function useLoadDSR() {
  const revisionsStore = useRevisionsStore();
  const loadedImagesStore = useLoadedImagesStore();
  const loadedVideosStore = useLoadedVideosStore();
  const { videos } = storeToRefs(loadedVideosStore);
  const sessionStore = useSessionStore();

  const loading = ref(true);
  const error = ref(false);
  const dsr: Ref<DealroomPublished> = ref(null);

  const pages: Ref<PageWithContent[]> = ref([]);
  const emptyPages: Ref<PageWithoutContent[]> = ref([]);
  const currentPageId: Ref<string> = ref(null);

  const currentPage = computed(
    () => pages.value.find((page: PageWithContent) => page.id === currentPageId.value) || null
  );

  const loadEditorImageURL = async (loadImagePayload: LoadImagePayload) => {
    try {
      const {
        url: srcUrl,
        externalId,
        blurhash,
      } = await DsrService.getEditorImageURL(loadImagePayload.imageId, ImageHeight.QHD);

      loadedImagesStore.setImage(loadImagePayload.imageId, srcUrl, externalId, blurhash);
      pages.value = setImageSrcURL(
        { pages: pages.value },
        {
          ...loadImagePayload,
          srcUrl,
          externalId,
          blurhash,
        }
      )?.pages as PageWithContent[];
    } catch (err) {
      console.error(err);
    }
  };

  const loadEditorImageURLs = (page: PageWithContent) => {
    const loadImagePayloads = (page.editorBlock && getLoadImageURLPayloads(page.editorBlock)) || [];
    loadImagePayloads.forEach(loadEditorImageURL);
  };

  const loadVideoThumbnails = (block: EditorBlock) => {
    const videoNodes = loadedVideosStore.findAllVideoNodesInBlock(block);
    videoNodes.forEach(async videoNode => {
      const video = videos.value.find(video => video.id === videoNode.videoId);
      if (!video) {
        const thumbUrl = await VideoService.loadThumbnail(videoNode.videoId);
        loadedVideosStore.setVideo(videoNode.videoId, videoNode.url, thumbUrl);
      }
    });
  };

  const setCurrentPage = (id: string) => {
    currentPageId.value = id;
    loadEditorImageURLs(currentPage.value);
    loadVideoThumbnails(currentPage.value?.editorBlock);
  };

  const initialize = () => {
    pages.value = pages.value.map(page => ({
      ...page,
      editorBlock: loadedImagesStore.mapImagesToBlock(page.editorBlock),
    }));

    if (pages.value[0]) {
      setCurrentPage(pages.value[0].id);
    }

    revisionsStore.setRevisionViewed();
  };

  const firstVisiblePage = computed(() => {
    const pagesWithSections = emptyPages.value.filter(
      page => page.editorBlock.content.sections.length
    );
    return pagesWithSections.find(page => page.isLandingPage) || pagesWithSections[0];
  });

  const loadFirstPage = async () => {
    const pageOffset = emptyPages.value.findIndex(page => firstVisiblePage.value.id === page.id);

    try {
      const editorBlock = await DsrService.loadEditorBlock(pageOffset);
      pages.value = [{ ...firstVisiblePage.value, editorBlock }];
      initialize();
    } catch (error) {
      console.error('Could not load first page', error);
    }
  };

  const loadRemainingContent = async () => {
    try {
      const pagesWithContent = await Promise.all(
        emptyPages.value.map(async (page, index) => {
          if (
            !page.editorBlock.content.sections.length ||
            pages.value.some(({ id }) => id === page.id)
          ) {
            return null;
          }

          const editorBlock = await DsrService.loadEditorBlock(index);
          return { ...page, editorBlock };
        })
      );

      pages.value.push(...pagesWithContent.filter(Boolean));

      pages.value.sort((a, b) => {
        const indexA = emptyPages.value.findIndex(({ id }) => id === a.id);
        const indexB = emptyPages.value.findIndex(({ id }) => id === b.id);
        return indexA - indexB;
      });

      emptyPages.value = [];
    } catch (e) {
      error.value = true;
      console.error('Could not load remaining content', e);
    } finally {
      loading.value = false;
    }
  };

  const loadDSR = async () => {
    try {
      [dsr.value, emptyPages.value] = await Promise.all([
        DsrService.load(),
        DsrService.loadPagesWithoutContent(),
      ]);
      sessionStore.setDealroomId(dsr.value.id);
      await loadFirstPage();
      loadRemainingContent();
    } catch (e) {
      error.value = true;
      console.error('Could not load DSR', e);
    } finally {
      loading.value = false;
    }
  };

  const loadNewPages = async () => {
    currentPageId.value = null;
    try {
      emptyPages.value = await DsrService.loadPagesWithoutContent();
      await loadFirstPage();
      loadRemainingContent();
    } catch (e) {
      error.value = true;
      console.error('Could not load new content', e);
    } finally {
      loading.value = false;
    }
  };

  const loadRoomSettings = async () => {
    try {
      const settings = await DsrService.loadRoomSettings();
      dsr.value = {
        ...dsr.value,
        settings,
      };
    } catch (e) {
      console.error(e);
    }
  };

  return {
    setCurrentPage,
    currentPage,
    currentPageId,
    loading,
    pages,
    emptyPages,
    dsr,
    error,
    loadDSR,
    loadNewPages,
    loadRoomSettings,
    loadEditorImageURLs,
    loadEditorImageURL,
    loadRemainingContent,
  };
}
